import React, { useMemo } from "react";
import { Box, CircularProgress, Alert, Typography, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  Chart as ChartJS,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  LineController,
  ScatterController,
} from "chart.js";
import { Chart } from "react-chartjs-2";

ChartJS.register(
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  LineController,
  ScatterController,
);

/**
 * Scatter plot of individual 1-5 star ratings per day,
 * with a per-day average line overlaid.
 *
 * Props:
 *   points       — [{ day: number, rating: number }]
 *   daysInMonth  — number  (sets x-axis upper bound)
 *   loading      — boolean
 *   error        — string
 *   height       — chart canvas height in px (default 120)
 *   emptyMessage — text shown when points is empty
 */
const ScatterRatingPlot = ({
  points = [],
  daysInMonth = 31,
  loading = false,
  error = "",
  height = 120,
  emptyMessage = "No satisfaction ratings recorded for this period.",
}) => {
  const theme = useTheme();
  // Chart.js defaults (#666 labels, black 10% grid) assume a light background.
  const axisColor = theme.palette.text.secondary;
  const gridColor = theme.palette.divider;

  const dailyAverages = useMemo(() => {
    const buckets = {};
    points.forEach(({ day, rating }) => {
      if (!buckets[day]) buckets[day] = [];
      buckets[day].push(rating);
    });
    return Object.entries(buckets)
      .map(([day, ratings]) => ({
        x: Number(day),
        y: ratings.reduce((sum, r) => sum + r, 0) / ratings.length,
      }))
      .sort((a, b) => a.x - b.x);
  }, [points]);

  const chartData = useMemo(
    () => ({
      datasets: [
        {
          type: "scatter",
          label: "Rating",
          data: points.map(({ day, rating }) => ({ x: day, y: rating })),
          backgroundColor: alpha(theme.palette.primary.main, 0.2),
          pointRadius: 5,
          pointHoverRadius: 7,
          order: 2,
          clip: false,
        },
        {
          type: "line",
          label: "Daily average",
          data: dailyAverages,
          borderColor: theme.palette.primary.main,
          backgroundColor: "transparent",
          pointRadius: 3,
          pointHoverRadius: 5,
          borderWidth: 2,
          tension: 0.35,
          fill: false,
          order: 1,
          clip: false,
        },
      ],
    }),
    [points, dailyAverages, theme.palette.primary.main],
  );

  const chartOptions = useMemo(
    () => ({
      animation: false,
      responsive: true,
      maintainAspectRatio: false,
      layout: { padding: { left: 0, right: 4, top: 10, bottom: 10 } },
      plugins: {
        legend: { display: false },
        tooltip: {
          displayColors: false,
          callbacks: {
            label: (context) => {
              if (context.dataset.type === "line") {
                return `Avg: ${context.parsed.y.toFixed(1)} ★`;
              }
              return `${context.parsed.y} ★`;
            },
          },
        },
      },
      scales: {
        x: {
          type: "linear",
          // Pad domain so day 1 / last day are not flush with the plot edge
          min: 0.5,
          max: daysInMonth + 0.5,
          // Hide the default bottom axis line (otherwise it sits under the 1★ grid line)
          border: { display: false },
          ticks: {
            color: axisColor,
            stepSize: 1,
            precision: 0,
            font: { size: 9 },
            maxTicksLimit: 16,
            autoSkip: true,
            callback: (v) => {
              const x = Number(v);
              const n = Math.round(x);
              if (Math.abs(x - n) > 1e-4) return "";
              if (n >= 1 && n <= daysInMonth) return String(n);
              return "";
            },
          },
          grid: { display: false },
          title: { display: true, text: "Day of month", color: axisColor, font: { size: 9 } },
        },
        y: {
          type: "linear",
          // Pad domain so 1★ / 5★ sit inside the plot (not on the clip edge)
          min: 0.5,
          max: 5.5,
          afterBuildTicks: (axis) => {
            axis.ticks = [1, 2, 3, 4, 5].map((value) => ({ value }));
          },
          grid: { color: gridColor },
          border: { color: gridColor },
          ticks: {
            color: axisColor,
            autoSkip: false,
            font: { size: 9 },
            callback: (v) => `${Math.round(Number(v))}★`,
          },
          title: { display: true, text: "Rating", color: axisColor, font: { size: 9 } },
        },
      },
    }),
    [daysInMonth, axisColor, gridColor],
  );

  if (error) return <Alert severity="error">{error}</Alert>;

  if (loading) {
    return (
      <Box sx={{ height, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <CircularProgress size={22} />
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ height, position: "relative" }}>
        <Chart type="scatter" data={chartData} options={chartOptions} />
      </Box>
      {!points.length ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          {emptyMessage}
        </Typography>
      ) : null}
    </>
  );
};

export default ScatterRatingPlot;
